// @spec SCF-005
// @spec SCF-008
// @spec IAM-STR-031
// @spec IAM-STR-035
// @spec SUP-STR-049
// Typed fetch wrapper with authentication support

import { useImpersonationStore } from '@/features/admin/stores/impersonation-store';
import { getPreferredLocaleHeader } from '@/lib/request-locale';
import { ApiClientError } from '@/types/api';

export interface ApiClientOptions {
  baseUrl?: string;
  /**
   * Function to get auth token. Supports both sync and async token retrieval.
   * For Clerk integration, pass the getToken function from useAuth().
   */
  getAuthToken?: () => string | null | Promise<string | null>;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  getBlob(path: string, options?: RequestOptions): Promise<Blob>;
  post<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  postBlob(path: string, data?: unknown, options?: RequestOptions): Promise<Blob>;
  postFormData<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T>;
  put<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
}

const API_V1_PREFIX = '/api/v1';
const LOCAL_DEV_BASE_URL_PATTERN =
  /^(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?(\/.*)?$/i;

function normalizeApiBaseUrlCandidate(rawValue: string): string {
  const candidate = rawValue.trim();

  if (!candidate) {
    return '';
  }

  if (candidate.startsWith('/') || /^[a-z][a-z\d+\-.]*:\/\//i.test(candidate)) {
    return candidate;
  }

  if (LOCAL_DEV_BASE_URL_PATTERN.test(candidate)) {
    return `http://${candidate}`;
  }

  return candidate;
}

/**
 * Normalize API base URL so callers can provide either:
 * - full prefix: http://localhost:8005/api/v1
 * - host only:   http://localhost:8005
 */
export function resolveApiBaseUrl(rawBaseUrl?: string): string {
  const candidate = normalizeApiBaseUrlCandidate(rawBaseUrl ?? import.meta.env.VITE_API_URL);
  if (!candidate) {
    return API_V1_PREFIX;
  }

  const withoutTrailingSlash = candidate.replace(/\/+$/, '');
  if (withoutTrailingSlash.endsWith(API_V1_PREFIX)) {
    return withoutTrailingSlash;
  }

  return `${withoutTrailingSlash}${API_V1_PREFIX}`;
}

/**
 * Normalize an API URL or path back to the path format expected by ApiClient methods.
 */
export function resolveApiPath(rawPathOrUrl: string, rawBaseUrl?: string): string {
  if (!rawPathOrUrl) {
    return '/';
  }

  const normalizedInput = (() => {
    try {
      return new URL(rawPathOrUrl).pathname + new URL(rawPathOrUrl).search;
    } catch {
      return rawPathOrUrl;
    }
  })();

  const apiPrefix = new URL(resolveApiBaseUrl(rawBaseUrl), 'http://localhost').pathname;
  if (normalizedInput.startsWith(apiPrefix)) {
    const suffix = normalizedInput.slice(apiPrefix.length);
    return suffix.startsWith('/') ? suffix : `/${suffix}`;
  }

  if (normalizedInput.startsWith('/')) {
    return normalizedInput;
  }

  return `/${normalizedInput}`;
}

/**
 * Creates a configured API client instance for making typed HTTP requests.
 *
 * @param options - Configuration options for the API client
 * @returns An ApiClient instance with typed HTTP methods
 *
 * @example
 * // With Clerk authentication
 * const { getToken } = useAuth();
 * const apiClient = createApiClient({
 *   getAuthToken: getToken,
 * });
 *
 * const users = await apiClient.get<User[]>('/users');
 * const newUser = await apiClient.post<User>('/users', { name: 'John' });
 */
interface ErrorResponse {
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
  // FastAPI wraps HTTPException detail in a `detail` key
  detail?: string | { message?: string; code?: string; details?: Record<string, unknown> };
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = resolveApiBaseUrl(options.baseUrl);

  async function request<T>(
    method: string,
    path: string,
    data?: unknown,
    requestOptions?: RequestOptions
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...requestOptions?.headers,
    };

    // Inject auth token if available (supports async token retrieval for Clerk)
    const token = await options.getAuthToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Inject impersonation token if active (for Super Admin impersonation)
    const impersonationToken = useImpersonationStore.getState().token;
    if (impersonationToken && !headers['X-Impersonation-Token']) {
      headers['X-Impersonation-Token'] = impersonationToken;
    }

    headers['Accept-Language'] ??= getPreferredLocaleHeader();

    const url = `${baseUrl}${path}`;
    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    // Only set signal if defined
    if (requestOptions?.signal) {
      fetchOptions.signal = requestOptions.signal;
    }

    // Only include body for methods that support it
    // @fixed IAM-STR-036: Allow body in DELETE requests for account deletion
    if (data !== undefined && method !== 'GET') {
      fetchOptions.body = JSON.stringify(data);
    }

    const response = await fetch(url, fetchOptions);

    // Handle error responses
    if (!response.ok) {
      let errorMessage = `Request failed with status ${String(response.status)}`;
      let errorCode: string | undefined;
      let errorDetails: Record<string, unknown> | undefined;

      try {
        const errorBody = (await response.json()) as ErrorResponse;
        // Handle FastAPI's detail wrapper: {"detail": {"message": "...", "code": "..."}}
        const detail = errorBody.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // FastAPI Pydantic validation errors: [{loc, msg, type}, ...]
          const messages = detail
            .map((e: { msg?: string; loc?: string[] }) => {
              const field = e.loc?.slice(-1)[0] ?? '';
              return field ? `${field}: ${e.msg ?? 'invalid'}` : (e.msg ?? 'invalid');
            })
            .join('; ');
          errorMessage = messages || errorMessage;
        } else if (detail && typeof detail === 'object') {
          if (detail.message) errorMessage = detail.message;
          if (detail.code) errorCode = detail.code;
          // Preserve detail.details if present, otherwise pass the full detail object
          // so extra fields like missingFields are accessible via error.details
          errorDetails = detail.details ?? detail as unknown as Record<string, unknown>;
        }
        // Also check root-level fields (non-FastAPI format)
        if (errorBody.message) {
          errorMessage = errorBody.message;
        }
        if (errorBody.code) errorCode = errorBody.code;
        if (errorBody.details) errorDetails = errorBody.details;
      } catch {
        // Response body is not JSON, use default message
      }

      throw new ApiClientError(response.status, errorMessage, errorCode, errorDetails);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  async function requestBlob(path: string, requestOptions?: RequestOptions): Promise<Blob> {
    const headers: Record<string, string> = {
      ...requestOptions?.headers,
    };

    // Inject auth token if available
    const token = await options.getAuthToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Inject impersonation token if active
    const impersonationToken = useImpersonationStore.getState().token;
    if (impersonationToken && !headers['X-Impersonation-Token']) {
      headers['X-Impersonation-Token'] = impersonationToken;
    }

    const url = `${baseUrl}${path}`;
    const fetchOptions: RequestInit = {
      method: 'GET',
      headers,
    };

    if (requestOptions?.signal) {
      fetchOptions.signal = requestOptions.signal;
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      let errorMessage = `Request failed with status ${String(response.status)}`;
      try {
        const errorBody = (await response.json()) as ErrorResponse;
        const detail = errorBody.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) {
          errorMessage = detail.message;
        }
        if (errorBody.message) {
          errorMessage = errorBody.message;
        }
      } catch {
        // Response body is not JSON
      }
      throw new ApiClientError(response.status, errorMessage);
    }

    return response.blob();
  }

  async function requestBlobPost(
    path: string,
    data?: unknown,
    requestOptions?: RequestOptions
  ): Promise<Blob> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...requestOptions?.headers,
    };

    // Inject auth token if available
    const token = await options.getAuthToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Inject impersonation token if active
    const impersonationToken = useImpersonationStore.getState().token;
    if (impersonationToken && !headers['X-Impersonation-Token']) {
      headers['X-Impersonation-Token'] = impersonationToken;
    }

    const url = `${baseUrl}${path}`;
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
    };

    if (data !== undefined) {
      fetchOptions.body = JSON.stringify(data);
    }

    if (requestOptions?.signal) {
      fetchOptions.signal = requestOptions.signal;
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      let errorMessage = `Request failed with status ${String(response.status)}`;
      try {
        const errorBody = (await response.json()) as ErrorResponse;
        const detail = errorBody.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) {
          errorMessage = detail.message;
        }
        if (errorBody.message) {
          errorMessage = errorBody.message;
        }
      } catch {
        // Response body is not JSON
      }
      throw new ApiClientError(response.status, errorMessage);
    }

    return response.blob();
  }

  async function requestFormData<T>(
    method: string,
    path: string,
    formData: FormData,
    requestOptions?: RequestOptions
  ): Promise<T> {
    const headers: Record<string, string> = {
      // Don't set Content-Type for FormData - browser sets it with boundary
      ...requestOptions?.headers,
    };

    // Inject auth token if available
    const token = await options.getAuthToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Inject impersonation token if active
    const impersonationToken = useImpersonationStore.getState().token;
    if (impersonationToken && !headers['X-Impersonation-Token']) {
      headers['X-Impersonation-Token'] = impersonationToken;
    }

    const url = `${baseUrl}${path}`;
    const fetchOptions: RequestInit = {
      method,
      headers,
      body: formData,
    };

    if (requestOptions?.signal) {
      fetchOptions.signal = requestOptions.signal;
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      let errorMessage = `Request failed with status ${String(response.status)}`;
      let errorCode: string | undefined;
      let errorDetails: Record<string, unknown> | undefined;

      try {
        const errorBody = (await response.json()) as ErrorResponse;
        const detail = errorBody.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          const messages = detail
            .map((e: { msg?: string; loc?: string[] }) => {
              const field = e.loc?.slice(-1)[0] ?? '';
              return field ? `${field}: ${e.msg ?? 'invalid'}` : (e.msg ?? 'invalid');
            })
            .join('; ');
          errorMessage = messages || errorMessage;
        } else if (detail && typeof detail === 'object') {
          if (detail.message) errorMessage = detail.message;
          if (detail.code) errorCode = detail.code;
          errorDetails = detail.details ?? detail as unknown as Record<string, unknown>;
        }
        if (errorBody.message) errorMessage = errorBody.message;
        if (errorBody.code) errorCode = errorBody.code;
        if (errorBody.details) errorDetails = errorBody.details;
      } catch {
        // Response body is not JSON
      }

      throw new ApiClientError(response.status, errorMessage, errorCode, errorDetails);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  return {
    get<T>(path: string, options?: RequestOptions): Promise<T> {
      return request<T>('GET', path, undefined, options);
    },

    getBlob(path: string, options?: RequestOptions): Promise<Blob> {
      return requestBlob(path, options);
    },

    post<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('POST', path, data, options);
    },

    postBlob(path: string, data?: unknown, options?: RequestOptions): Promise<Blob> {
      return requestBlobPost(path, data, options);
    },

    postFormData<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
      return requestFormData<T>('POST', path, formData, options);
    },

    put<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('PUT', path, data, options);
    },

    patch<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('PATCH', path, data, options);
    },

    // @fixed IAM-STR-036: Support body in DELETE requests for account deletion
    delete<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('DELETE', path, data, options);
    },
  };
}

/**
 * Default API client instance using environment configuration.
 * For authenticated requests, use createApiClient with getAuthToken.
 */
export const apiClient = createApiClient();
